import React, { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';
import { FinalButton } from '../../../Components/FinalButton';
import { InputRHF, SelectRHF, TextareaRHF, ToggleRHF } from '../../../Components/Form';
import { Text } from '../../../globalStyles';
import { useGetSaiSurveyAssessmentOptionsQuery } from '../../../graphQL';
import { getTermOptions } from './surveyUtils';

interface SurveyPeriodFormProps {
  isEditing: boolean;
  onSubmit: () => void;
  onCancel: () => void;
  createLoading: boolean;
  updateLoading: boolean;
  errorMessage?: string;
  calendarYear: string;
  academicYear: string;
  dateFieldsRef?: React.RefObject<HTMLDivElement>;
}

export const SurveyPeriodForm = ({
  isEditing,
  onSubmit,
  onCancel,
  createLoading,
  updateLoading,
  errorMessage,
  calendarYear,
  academicYear,
  dateFieldsRef,
}: SurveyPeriodFormProps): JSX.Element => {
  const form = useFormContext();

  // Subscribe to form state changes more explicitly and reliably
  const { isDirty, dirtyFields } = form.formState;

  // Watch custom content toggle to conditionally show title/description
  const customContentEnabled = form.watch('customContentEnabled');

  // As a fallback, also check if any fields are dirty manually
  const hasAnyDirtyFields = Object.keys(dirtyFields).length > 0;
  const formIsDirty = isDirty || hasAnyDirtyFields;

  // Fetch survey content versions from backend
  const { data: surveyVersionsData, loading: versionsLoading } =
    useGetSaiSurveyAssessmentOptionsQuery();

  // Memoize survey version options
  const surveyVersionOptions = useMemo(
    () =>
      surveyVersionsData?.getSaiSurveyAssessmentOptions?.map(version => ({
        id: version,
        label: version,
      })) || [],
    [surveyVersionsData]
  );

  // Set default survey version when options are loaded and we're creating (not editing)
  useEffect(() => {
    const currentValue = form.getValues('surveyContentVersion');

    if (surveyVersionOptions.length > 0 && !currentValue && !isEditing) {
      form.setValue('surveyContentVersion', surveyVersionOptions[0].id, {
        shouldDirty: false,
        shouldValidate: true,
      });
    }
  }, [surveyVersionOptions, form, isEditing]);

  const termOptions = useMemo(() => getTermOptions(), []);

  return (
    <form onSubmit={onSubmit}>
      <Section first>
        <InputRHF
          name="surveyName"
          placeholder="Survey Display Name"
          rules={{ required: 'Survey name is required' }}
          controlProps={{
            label: 'Survey Display Name (MCP and student facing)',
            subtitle:
              'Display name for this survey period (e.g., "Fall 2025 Pulse Survey") - This will be visible to MCP and Student users',
            required: true,
          }}
        />
      </Section>

      <Section>
        <FlexRow ref={dateFieldsRef}>
          <FlexItem>
            <InputRHF
              name="startDate"
              type="date"
              rules={{ required: 'Start Date is required' }}
              controlProps={{
                label: 'Start Date',
                subtitle: 'When the survey period begins',
                required: true,
              }}
            />
          </FlexItem>
          <FlexItem>
            <InputRHF
              name="endDate"
              type="date"
              rules={{
                required: 'End Date is required',
                validate: value => {
                  const start = form.getValues('startDate');
                  if (start && value) {
                    const s = new Date(start);
                    const e = new Date(value);
                    if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && e <= s) {
                      return 'End Date must be after Start Date';
                    }
                  }
                  return true;
                },
              }}
              controlProps={{
                label: 'End Date',
                subtitle: 'When the survey period ends',
                required: true,
              }}
            />
          </FlexItem>
        </FlexRow>
      </Section>

      <Section>
        <FlexRow>
          <FlexItem>
            <SelectRHF
              name="term"
              options={termOptions}
              rules={{ required: 'Term is required' }}
              controlProps={{
                label: 'Term',
                subtitle: 'Academic term for this survey period',
                required: true,
              }}
            />
          </FlexItem>
        </FlexRow>
      </Section>

      <Section>
        <FlexRow>
          <FlexItem as={FlexRow} style={{ flex: 1, gap: '1rem' }}>
            <FlexItem>
              <ReadonlyLabel>Calendar Year</ReadonlyLabel>
              <ReadonlyInput
                type="text"
                value={calendarYear}
                disabled
                readOnly
                placeholder="Automatically calculated"
              />
              <MutedCaption>Automatically calculated from start date</MutedCaption>
            </FlexItem>
            <FlexItem>
              <ReadonlyLabel>Academic Year</ReadonlyLabel>
              <ReadonlyInput
                type="text"
                value={academicYear}
                disabled
                readOnly
                placeholder="Automatically calculated"
              />
              <MutedCaption>Automatically calculated from term and start date</MutedCaption>
            </FlexItem>
          </FlexItem>
        </FlexRow>
      </Section>

      <Section>
        <SelectRHF
          name="surveyContentVersion"
          options={surveyVersionOptions}
          disabled={versionsLoading}
          rules={{ required: 'Survey Version is required' }}
          controlProps={{
            label: 'Survey Version',
            subtitle: versionsLoading
              ? 'Loading survey versions...'
              : 'Version of the survey content to use',
            required: true,
          }}
        />
        {/* Display questionnaires when a survey version is selected */}
        {/* <SurveyQuestionnaires surveyVersion={surveyContentVersion} /> */}
      </Section>

      <Section>
        <InputRHF
          name="targetCohortName"
          placeholder="First year students, 18 y/o+"
          rules={{ maxLength: { value: 100, message: 'Input must be 100 characters or less.' } }}
          controlProps={{
            label: 'Target Cohort Name',
            subtitle:
              'The target cohort of students being served by this survey. Visible in partner-facing reporting. Examples: ‘First year students’, ‘All full-time undergraduate students’, or ‘Residential students’.',
          }}
        />
        <InputRHF
          name="numberOfStudentsInCohort"
          placeholder="1960"
          step={1}
          min={0}
          rules={{
            min: { value: 0, message: 'Invalid entry. Please enter a positive whole number.' },
            max: {
              value: 2147483647,
              message: 'Value is too large. Please enter a smaller number.',
            },
            validate: (value: number | string | undefined) => {
              if (value === undefined || value === '') return true;
              const num = Number(value);
              if (!Number.isInteger(num)) {
                return 'Invalid entry. Please enter a positive whole number.';
              }
              return true;
            },
          }}
          controlProps={{
            label: 'Number of Students in Eligible Cohort',
            subtitle:
              'Total number of students eligible to complete this survey during this survey period.',
          }}
        />
        <InputRHF
          name="goalResponseRate"
          placeholder="30% of cohort (529 students)"
          rules={{ maxLength: { value: 100, message: 'Input must be 100 characters or less.' } }}
          controlProps={{
            label: 'Goal Response Rate',
            subtitle:
              'Partner’s goal cohort response rate for this survey period. Visible in partner-facing reporting. Examples: ‘30% of target cohort’, ‘40% (2000 students)’, ‘25% of target cohort’.',
          }}
        />
      </Section>

      <Section>
        <ToggleRHF name="customContentEnabled">
          <label className="db">
            <Text.bodyBold>Show Custom Content Screen after Consent Page</Text.bodyBold>
            <Text.bodySmall>
              Show a screen with custom text after the survey consent intro screen, but before the
              first questionnaire. Use this screen to provide custom messaging for a given survey
              period for an org.
            </Text.bodySmall>
          </label>
        </ToggleRHF>
        {customContentEnabled && (
          <>
            <InputRHF
              name="customContentTitle"
              placeholder="Before you start, here's a few things you should know"
              rules={{
                required: 'A heading for the Custom Content Screen is required.',
                maxLength: { value: 200, message: 'Input must be 200 characters or less.' },
              }}
              controlProps={{
                label: 'Custom Content Screen - Title',
                required: true,
                subtitle:
                  'Required. This text will appear as the heading on the custom content screen, which appears after the intro consent screen. Max 200 char.',
              }}
            />
            <TextareaRHF
              name="customContentDescription"
              placeholder={`This survey is conducted each fall and spring to help us understand trends in student wellbeing and campus life.\n\nIf these questions look familiar, that's intentional—asking the same questions twice a year lets us see meaningful patterns over time.`}
              rules={{
                maxLength: { value: 1000, message: 'Input must be 1000 characters or less.' },
              }}
              controlProps={{
                label: 'Custom Content Screen - Description',
                subtitle:
                  'Optional. This text will appear as the body text on the custom content screen. Line breaks are permitted in order to display text as paragraphs. Max 1000 char.',
              }}
            />
          </>
        )}
      </Section>

      <ActionsRow>
        <FinalButton
          kind="primary"
          type="submit"
          loading={isEditing ? updateLoading : createLoading}
          disabled={!formIsDirty}
        >
          {isEditing ? 'Update Survey Period' : 'Create Survey Period'}
        </FinalButton>
        <FinalButton kind="outline_black" onClick={onCancel}>
          Cancel
        </FinalButton>
      </ActionsRow>

      {errorMessage && (
        <ErrorRow>
          <Text.bodySmall kind="danger">
            Error {isEditing ? 'updating' : 'creating'} survey period: {errorMessage}
          </Text.bodySmall>
        </ErrorRow>
      )}
    </form>
  );
};

const Section = styled.div<{ first?: boolean }>`
  margin-top: ${({ first }) => (first ? 0 : '2rem')};
`;
const FlexRow = styled.div`
  display: flex;
  gap: 1rem;
`;
const FlexItem = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
`;
const ReadonlyLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: bold;
`;
const ReadonlyInput = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #ccc;
  border-radius: 0.25rem;
  background-color: #f5f5f5;
  color: #666;
  font: inherit;
`;
const MutedCaption = styled(Text.bodySmall)`
  margin-top: 0.25rem;
  color: #666;
`;
const ActionsRow = styled.div`
  margin-top: 2rem;
  display: flex;
  gap: 0.75rem;
`;
const ErrorRow = styled.div`
  margin-top: 1rem;
`;
